import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  X,
  ChevronDown,
  ChevronRight,
  Newspaper,
  BookOpen,
  PlayCircle,
  Bookmark,
  CalendarDays,
  Settings,
  HelpCircle,
  Plug,
  Sparkles,
  AlertTriangle,
  BarChart2,
  Shield,
  Building,
  Users,
  Filter,
  Key,
  Palette,
  Activity,
  MessageSquare,
  Bell,
  Brain,
  Server,
  BarChart3,
  TrendingUp,
  BookMarked,
  LineChart,
  ClipboardList,
  LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { usePermissionContext } from '../contexts/PermissionContext';

interface MobileMenuOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

interface NavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  exact?: boolean;
}

const MobileMenuOverlay: React.FC<MobileMenuOverlayProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const { roles: permissionRoles } = usePermissionContext();
  const isOrgMember = !user?.is_admin && permissionRoles.some((r) =>
    ['org_admin', 'lead_analyst', 'analyst', 'viewer'].includes(r)
  );
  const isOrgAdminNav = permissionRoles.includes('org_admin');
  const isLeadAnalystOrAdmin = permissionRoles.some((r) => ['lead_analyst', 'org_admin'].includes(r));

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const directItems: { label: string; icon: LucideIcon; path: string; exact?: boolean; badge?: string }[] = useMemo(() => [
    { label: 'Appointments', icon: CalendarDays, path: '/trading/appointments' },
    { label: 'FAQ', icon: BookOpen, path: '/faq' },
    { label: 'Support', icon: HelpCircle, path: '/support/tickets' },
    { label: 'Settings', icon: Settings, path: '/settings', exact: true },
    {
      label: 'API Access',
      icon: Plug,
      path: '/settings/mcp',
      badge: !isPro ? 'PRO+' : undefined,
    },
    { label: "What's New", icon: Sparkles, path: '/releases' },
  ], [isPro]);

  if (!isOpen) return null;

  const navGroups: NavGroup[] = [
    {
      key: 'news',
      label: 'News & Calendar',
      icon: Newspaper,
      items: [
        { label: 'Forex Calendar', icon: AlertTriangle, path: '/news' },
        { label: 'Forex Pairs', icon: BarChart2, path: '/forex-pairs' },
      ],
    },
    {
      key: 'learn',
      label: 'Learn',
      icon: BookOpen,
      items: [
        { label: 'Videos', icon: PlayCircle, path: '/knowledge' },
        { label: 'Glossary', icon: Bookmark, path: '/knowledge/glossary' },
      ],
    },
    {
      key: 'trading',
      label: 'Trading',
      icon: TrendingUp,
      items: [
        { label: 'Dashboard', icon: LineChart, path: '/trading', exact: true },
        { label: 'Strategies', icon: BookMarked, path: '/trading/strategies' },
        { label: 'Performance', icon: BarChart3, path: '/trading/performance' },
        { label: 'Trades', icon: ClipboardList, path: '/trading/trades' },
      ],
    },
  ];

  if (isOrgMember) {
    const orgItems: NavItem[] = [];
    if (isOrgAdminNav) orgItems.push({ label: 'Members', icon: Users, path: '/org/members' });
    if (isOrgAdminNav) orgItems.push({ label: 'Billing', icon: Building, path: '/org/billing' });
    if (isLeadAnalystOrAdmin) orgItems.push({ label: 'Signal Queue', icon: Filter, path: '/org/signals/queue' });
    if (isOrgAdminNav) orgItems.push({ label: 'MCP API Keys', icon: Key, path: '/org/settings/mcp-keys' });
    if (isOrgAdminNav) orgItems.push({ label: 'Branding', icon: Palette, path: '/org/settings/branding' });
    if (orgItems.length > 0) {
      navGroups.push({ key: 'org', label: 'Organisation', icon: Building, items: orgItems });
    }
  }

  if (user?.is_admin) {
    navGroups.push({
      key: 'admin',
      label: 'Admin',
      icon: Shield,
      items: [
        { label: 'Dashboard', icon: Shield, path: '/admin' },
        { label: 'Organisations', icon: Building, path: '/admin/organisations' },
        { label: 'Roles', icon: Shield, path: '/admin/access-control/roles' },
        { label: 'Background Tasks', icon: Activity, path: '/admin/background-tasks' },
        { label: 'Services', icon: Server, path: '/admin/services' },
        { label: 'Performance', icon: BarChart3, path: '/admin/performance' },
        { label: 'Exceptions', icon: AlertTriangle, path: '/admin/exceptions' },
        { label: 'Support Tickets', icon: MessageSquare, path: '/admin/support' },
        { label: 'Announcements', icon: Bell, path: '/admin/announcements' },
        { label: 'Personas', icon: Brain, path: '/settings/personas' },
      ],
    });
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden bg-slate-900 overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700/50 sticky top-0 bg-slate-900">
        <button
          type="button"
          onClick={onClose}
          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-slate-700/50 transition-colors text-slate-300"
          aria-label="Close menu"
        >
          <X size={22} />
        </button>
        <span className="font-semibold text-lg">Menu</span>
      </div>

      {/* Direct items */}
      <div className="p-2">
        {directItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path, item.exact);
          return (
            <button
              type="button"
              key={item.path}
              onClick={() => handleNav(item.path)}
              style={active ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
              className={`w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg transition-colors text-left relative
                ${active ? '' : 'text-slate-300 hover:bg-slate-700/50'}`}
            >
              <Icon size={20} />
              <span className="font-medium">{item.label}</span>
              {item.badge && (
                <span className="ml-auto text-[9px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded px-1 py-0.5">
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Expandable groups */}
      <div className="p-2 pt-0">
        {navGroups.map((group) => {
          const GroupIcon = group.icon;
          const isGroupActive = group.items.some((item) => isActive(item.path));
          const isExpanded = expanded[group.key] ?? isGroupActive;

          return (
            <div key={group.key} className="mb-1">
              <button
                type="button"
                onClick={() => toggle(group.key)}
                style={isGroupActive ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                className={`w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg transition-colors
                  ${isGroupActive ? '' : 'text-slate-300 hover:bg-slate-700/50'}`}
              >
                <GroupIcon size={20} />
                <span className="font-medium flex-1 text-left">{group.label}</span>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>

              {isExpanded && (
                <div className="ml-6 border-l border-slate-700/50 pl-2 mt-1 space-y-0.5">
                  {group.items.map((item) => {
                    const ItemIcon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        type="button"
                        key={item.path}
                        onClick={() => handleNav(item.path)}
                        style={active ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                        className={`w-full flex items-center gap-2 px-3 py-2 min-h-[44px] rounded-md text-sm transition-colors text-left
                          ${active ? '' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'}`}
                      >
                        <ItemIcon size={16} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileMenuOverlay;
